import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Landmark, ArrowDownRight, PiggyBank, AlertTriangle, RefreshCw } from 'lucide-react';
import { shortDate } from '@/lib/utils';
import {
  getWhopHirePayments,
  getWhopHireStats,
  type WhopHirePaymentRow,
} from '@/lib/workerApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataPagination } from '@/components/DataPagination';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Whop payment lifecycle statuses — same enum as the subscription tab (both
// scans read the same /api/v2/payments). `statuses` is the only filter here
// too, for the same reason: it's the one Whop applies server-side.
type StatusTab = 'all' | 'paid' | 'open' | 'pending' | 'void' | 'uncollectible';
const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'all',           label: 'All' },
  { key: 'paid',          label: 'Paid' },
  { key: 'open',          label: 'Open' },
  { key: 'pending',       label: 'Pending' },
  { key: 'uncollectible', label: 'Uncollectible' },
  { key: 'void',          label: 'Void' },
];

function usd(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return null;
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ChargeStatusBadge({ row }: { row: WhopHirePaymentRow }) {
  const label = row.substatus || row.status || 'unknown';
  const cls =
    label === 'succeeded' || label === 'paid'
      ? 'bg-green-100 text-green-800 border-green-200'
      : label === 'failed' || label === 'uncollectible'
        ? 'bg-red-100 text-red-800 border-red-200'
        : label === 'pending' || label === 'open'
          ? 'bg-amber-100 text-amber-800 border-amber-200'
          : label === 'void' || label === 'canceled'
            ? 'bg-slate-100 text-slate-700 border-slate-200'
            : 'bg-gray-100 text-gray-700 border-gray-200';
  return <Badge className={`border ${cls} capitalize`}>{label}</Badge>;
}

/** cr_hires.status — a different lifecycle than the Whop charge status above
 *  (e.g. a 'paid' Whop charge can back a 'partial_completed' hire). */
function HireStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const cls =
    status === 'completed'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
      : status === 'partial_completed'
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : status === 'confirmed'
          ? 'bg-blue-100 text-blue-800 border-blue-200'
          : status === 'failed' || status === 'cancelled'
            ? 'bg-red-100 text-red-800 border-red-200'
            : 'bg-slate-100 text-slate-700 border-slate-200';
  return <Badge className={`border ${cls} text-[10px] px-1.5 py-0 capitalize`}>{status.replace(/_/g, ' ')}</Badge>;
}

/** Card brand + last 4 when a card was used; otherwise the raw payment method type. */
function MethodCell({ row }: { row: WhopHirePaymentRow }) {
  if (row.card_brand) {
    return (
      <span className="text-xs whitespace-nowrap">
        <span className="capitalize">{row.card_brand}</span>
        {row.card_last4 && <span className="text-muted-foreground"> •••• {row.card_last4}</span>}
      </span>
    );
  }
  if (row.payment_method_type) {
    return <span className="text-xs text-muted-foreground capitalize">{row.payment_method_type}</span>;
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

function CreatorCell({ row }: { row: WhopHirePaymentRow }) {
  if (!row.creator_name) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div>
      <div className="font-medium truncate">{row.creator_name}</div>
      {row.creator_tiktok_username && (
        <div className="text-[11px] text-muted-foreground">@{row.creator_tiktok_username}</div>
      )}
    </div>
  );
}

type StatAccent = 'emerald' | 'rose' | 'amber';

// Same shape/pattern as WhopSubscriptionsPanel's and SystemSubscriptionsPanel's
// STAT_ACCENTS — kept local rather than shared (see those files' comments) —
// but with rose/amber instead of sky/purple: this panel's three cards aren't
// a brand-vs-agency split, they're gross amount vs. what left as Whop's fee
// vs. what BMC kept, so the accents read as "in / out / kept" instead of
// "category A / category B".
const STAT_ACCENTS: Record<StatAccent, { tile: string; value: string; glow: string; pill: string; bar: string }> = {
  emerald: {
    tile: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    value: 'text-emerald-600 dark:text-emerald-400',
    glow: 'from-emerald-500/[0.12]',
    pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    bar: 'bg-emerald-500',
  },
  rose: {
    tile: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    value: 'text-rose-600 dark:text-rose-400',
    glow: 'from-rose-500/[0.12]',
    pill: 'bg-rose-100 text-rose-700 border border-rose-200',
    bar: 'bg-rose-500',
  },
  amber: {
    tile: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    value: 'text-amber-600 dark:text-amber-400',
    glow: 'from-amber-500/[0.12]',
    pill: 'bg-amber-100 text-amber-700 border border-amber-200',
    bar: 'bg-amber-500',
  },
};

/**
 * Premium tile for the three revenue totals — same rounded-xl + corner-glow
 * treatment as the subscription tabs' StatCard. The paid-charge count is a
 * colored pill; a truncated scan shows "N+" with a tooltip, same as
 * WhopSubscriptionsPanel (this tab reads live from Whop too, so it's subject
 * to the same scan cap).
 */
function StatCard({ icon: Icon, label, value, count, countLabel = 'paid', truncated, sub, accent, segments, loading, loadingHint }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  count?: number;
  countLabel?: string;
  truncated?: boolean;
  sub?: string;
  accent: StatAccent;
  /** This card's share of Total Amount, as filled segments (0-100 each)
   *  under the pill row — e.g. Whop's cut in rose, BMC's cut in amber. */
  segments?: { pct: number; className: string }[];
  loading?: boolean;
  /** Shown in place of the pill/caption while loading — for a card whose
   *  slowness needs explaining (this endpoint's full-scan fee enrichment
   *  takes ~30s), not just a bare skeleton. */
  loadingHint?: string;
}) {
  const a = STAT_ACCENTS[accent];
  return (
    <div className="group relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm transition-shadow duration-200 hover:shadow-elevate-lg">
      <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-2xl transition-opacity duration-200 group-hover:opacity-125`} />
      <div className="relative space-y-3">
        <div className="flex items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${a.tile}`}>
            <Icon className="h-4 w-4" />
          </span>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>

        {loading ? (
          <Skeleton className="h-9 w-32" />
        ) : (
          <p className={`text-3xl font-bold leading-none tracking-tight tabular-nums ${a.value}`}>{value}</p>
        )}

        <div className="flex min-h-[22px] flex-wrap items-center gap-2">
          {loading ? (
            <Skeleton className="h-[22px] w-24 rounded-full" />
          ) : (
            <>
              {count !== undefined && (
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${a.pill} ${truncated ? 'cursor-help' : ''}`}>
                        {count.toLocaleString()}{truncated ? '+' : ''} {countLabel}
                      </span>
                    </TooltipTrigger>
                    {truncated && (
                      <TooltipContent side="top" className="max-w-xs">
                        More hire charges exist on Whop than were scanned — this count (and the amount
                        above) is a floor, not the true total.
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
            </>
          )}
        </div>

        {/* Shown instead of (not under) the skeletons above — this endpoint
            does a full-scan Whop fee enrichment pass (~30s at current
            volume), so silence here reads as broken rather than slow. */}
        {loading && loadingHint && (
          <p className="text-[11px] text-muted-foreground -mt-1">{loadingHint}</p>
        )}

        {!loading && segments && segments.length > 0 && (
          <div className="flex h-1 w-full overflow-hidden rounded-full bg-muted">
            {segments.map((s, i) => (
              <div
                key={i}
                className={`h-full transition-all duration-500 ${s.className}`}
                style={{ width: `${Math.max(0, Math.min(s.pct, 100))}%` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Creator-hire charges read LIVE from Whop — one row per chargeBrandForHire()
 * call, which brand paid it, and which creator/campaign it funded. Sibling
 * to WhopSubscriptionsPanel: same product-excludes-the-other's-product
 * relationship (a hire charge and a subscription charge can never be the
 * same payment — see listHirePayments/listSubscriptionPayments), same
 * status-only filtering, same split between a fast listing call and a
 * separate stats call.
 *
 * The stats call here is genuinely slower than the subscription tab's: Whop
 * has no equivalent of "fee" on its list endpoint, so getting an exact total
 * fee means enriching every scanned row, not just the visible page — see
 * getWhopHireStats. staleTime is set generously so switching tabs and back
 * doesn't repeat that cost.
 */
export default function HiringTransactionsPanel() {
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['whop-hire-payments', statusTab, page, pageSize],
    queryFn: () => getWhopHirePayments({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      statuses: statusTab === 'all' ? undefined : [statusTab],
    }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['whop-hire-stats'],
    queryFn: () => getWhopHireStats(),
    // Long stale time — this call enriches every scanned row (no v2
    // shortcut for fee, unlike amount), so it's genuinely expensive. Not
    // worth repeating every time this tab is revisited within a few minutes.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const rows       = data?.rows ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const statusCounts = statsData?.status_counts ?? {};

  // Whop's cut and BMC's cut as a share of the total paid amount, for the
  // stat cards' proportion bars. Zero (not NaN) while stats haven't loaded or
  // nothing's been collected yet.
  const totalCents    = statsData?.stats.total_amount_cents ?? 0;
  const feeCents      = statsData?.stats.total_fee_cents ?? 0;
  const platformCents = statsData?.stats.total_platform_fee_cents ?? 0;
  const feeShare      = totalCents > 0 ? (feeCents / totalCents) * 100 : 0;
  const platformShare = totalCents > 0 ? (platformCents / totalCents) * 100 : 0;
  // Paid-charge count behind each fee total specifically — not the same as
  // total_paid_count when Whop's per-payment enrichment failed for some rows
  // (see fee_unavailable_count / platform_fee_unavailable_count banners
  // below), so the pill never claims more charges than actually landed in
  // that particular sum.
  const paidCount = statsData?.stats.total_paid_count;
  const knownFeeCount = paidCount !== undefined
    ? paidCount - (statsData?.stats.fee_unavailable_count ?? 0)
    : undefined;
  const knownPlatformCount = paidCount !== undefined
    ? paidCount - (statsData?.stats.platform_fee_unavailable_count ?? 0)
    : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Creator-hire charges, read live from Whop — which brand paid, which creator and campaign
          it funded, Whop's fee, and what BMC kept. Covers hires that were charged on Whop; one
          fully covered by wallet credit never creates a Whop payment, so it doesn&apos;t appear here.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Refreshing' : 'Refresh'}
        </Button>
      </div>

      {/* All three totals are 'paid' charges only — the rest (open/pending/
          void/uncollectible) never collected anything. Total Whop Fee needs a
          full per-row enrichment pass (Whop has no list-level fee field at
          all), which is why these cards can take much longer to resolve than
          the subscription tab's equivalent — see loadingHint below. The bar
          under Total Amount splits into Whop's cut (rose) and BMC's cut
          (amber); Whop Fee and Platform Fee each show their own share. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard
          icon={Landmark}
          label="Total Amount"
          value={statsLoading ? '—' : usd(totalCents)}
          count={paidCount}
          truncated={statsData?.truncated}
          sub={statsLoading || totalCents === 0 ? undefined : null }
          accent="emerald"
          segments={[
            { pct: feeShare, className: 'bg-rose-500' },
            { pct: platformShare, className: 'bg-amber-500' },
          ]}
          loading={statsLoading}
        />
        <StatCard
          icon={ArrowDownRight}
          label="Total Whop Fee"
          value={statsLoading ? '—' : usd(feeCents)}
          count={knownFeeCount}
          countLabel="with fee data"
          sub="what Whop kept"
          accent="rose"
          segments={[{ pct: feeShare, className: 'bg-rose-500' }]}
          loading={statsLoading}
          loadingHint="Enriching every charge — can take a while"
        />
        <StatCard
          icon={PiggyBank}
          label="Total Platform Fee"
          value={statsLoading ? '—' : usd(platformCents)}
          count={knownPlatformCount}
          countLabel="with margin data"
          // sub="BMC margin"
          accent="amber"
          segments={[{ pct: platformShare, className: 'bg-amber-500' }]}
          loading={statsLoading}
          loadingHint="Enriching every charge — can take a while"
        />
      </div>

      {!!statsData?.stats.fee_unavailable_count && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-800">
            Whop's fee lookup failed for {statsData.stats.fee_unavailable_count} charge(s), so the
            Total Whop Fee above is a floor, not exact.
          </p>
        </div>
      )}

      {/* A deleted hire row leaves its Whop payment with no resolvable margin,
          so BMC's take on it can't be computed. Disclosed rather than quietly
          dropped from the total. */}
      {!!statsData?.stats.platform_fee_unavailable_count && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-800">
            {statsData.stats.platform_fee_unavailable_count} paid charge(s) have no resolvable hire
            record, so their margin is unknown and the Total Platform Fee above is a floor, not exact.
          </p>
        </div>
      )}

      {(data?.truncated || statsData?.truncated) && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-800">
            More hire charges exist on Whop than were scanned, so the count{data?.truncated && statsData?.truncated ? 's' : ''} above{' '}
            {data?.truncated && statsData?.truncated ? 'are floors' : 'is a floor'} rather than the true total.
          </p>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="flex items-center gap-1 flex-wrap border-b px-4 pt-1 pb-1">
          {STATUS_TABS.map(({ key, label }) => {
            const count = key === 'all'
              ? statsData?.total_count ?? Object.values(statusCounts).reduce((s, n) => s + Number(n), 0)
              : Number(statusCounts[key] ?? 0);
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setStatusTab(key); setPage(1); }}
                className={`px-3 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  statusTab === key
                    ? 'border-b-2 border-foreground text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
                <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${statusTab === key ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'}`}>
                  {statsLoading ? '…' : count}
                </span>
              </button>
            );
          })}
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center py-16 text-sm gap-3 px-6 text-center">
            <AlertTriangle className="h-10 w-10 text-red-400" />
            <p className="font-medium text-red-700">Could not load hire charges from Whop.</p>
            <p className="text-muted-foreground max-w-md">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : totalItems === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <Landmark className="h-10 w-10 opacity-25 mb-2" />
            <p className="font-medium">No hire charges found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Brand</TableHead>
                    <TableHead className="whitespace-nowrap">Creator</TableHead>
                    <TableHead className="whitespace-nowrap">Campaign</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Package</TableHead>
                    <TableHead className="whitespace-nowrap">Hire Status</TableHead>
                    <TableHead className="whitespace-nowrap">Charge Status</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Whop Fee</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Creator Payout</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Platform Fee</TableHead>
                    <TableHead className="whitespace-nowrap">Method</TableHead>
                    <TableHead className="whitespace-nowrap">Paid</TableHead>
                    <TableHead className="whitespace-nowrap">Hire ID</TableHead>
                    <TableHead className="whitespace-nowrap">Payment ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, idx) => {
                    const amount = usd(row.amount_cents);
                    const refunded = row.refunded_amount_cents && row.refunded_amount_cents > 0
                      ? usd(row.refunded_amount_cents)
                      : null;
                    return (
                      <TableRow key={row.payment_id ?? `row-${idx}`}>
                        <TableCell className="max-w-[180px] truncate font-medium">
                          {row.brand_name ?? '—'}
                        </TableCell>
                        <TableCell className="max-w-[180px]"><CreatorCell row={row} /></TableCell>
                        <TableCell className="max-w-[180px]">
                          <div className="text-sm truncate">{row.campaign_name ?? '—'}</div>
                          {row.product_name && (
                            <div className="text-[11px] text-muted-foreground truncate">{row.product_name}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{row.package_size ?? '—'}</TableCell>
                        <TableCell><HireStatusBadge status={row.hire_status} /></TableCell>
                        <TableCell><ChargeStatusBadge row={row} /></TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {amount === null ? (
                            <span className="text-xs text-amber-700">unavailable</span>
                          ) : (
                            <>
                              <div className="font-medium">{amount}</div>
                              {refunded && (
                                <div className="text-[11px] font-medium text-red-600">
                                  −{refunded} refunded
                                </div>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap text-muted-foreground text-sm">
                          {row.fee_cents !== null ? usd(row.fee_cents) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap text-muted-foreground text-sm">
                          {row.creator_amount_cents !== null ? usd(row.creator_amount_cents) : '—'}
                        </TableCell>
                        {/* Two different nulls, shown differently: an unsettled
                            charge earned BMC nothing (a dash), whereas a settled
                            one whose hire record is gone has an unknowable margin
                            (flagged). Neither is rendered as $0. */}
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {row.platform_fee_cents !== null ? (
                            <span className="font-medium text-emerald-700">{usd(row.platform_fee_cents)}</span>
                          ) : row.status !== 'paid' ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <span className="text-xs text-amber-700">unavailable</span>
                          )}
                        </TableCell>
                        <TableCell><MethodCell row={row} /></TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {row.paid_at ? shortDate(row.paid_at) : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {row.hire_id ?? '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {row.payment_id ?? '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <DataPagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>
    </div>
  );
}
